import { API_URL } from 'env';
import type { User } from 'types/user';
import React, { createContext, useContext, useState, ReactNode } from 'react';


interface TradeAccount {
    id: string;
    login_id: string;
    nickname: string;
    platform: string;
    broker: string;
    balance: number;
    equity: number;
    pl: number;
    linked_user: {
        id: string;
        name: string;
        email: string;
    } | null;
}
type DataContextType = {
    users: User[];
    tradeAccounts: TradeAccount[];
    total_balance: { account: string, name: string, total: number }[];
    total_trade: number;
    allAccountBalance: number;
    totalAccounts: number;
    activeUsers: number;
    tradeHistoryChartData: { label: string; name: string; data: { x: Date, y: number }[] }[];
    assignedAccountUser?: { user: { id: string, name: string, email: string }, account: { id: string, login_id: string, nickname: string, platform: string, broker: string, balance: number } }[];
    setUsers: React.Dispatch<React.SetStateAction<User[]>>;
    setTradeAccounts: React.Dispatch<React.SetStateAction<TradeAccount[]>>;
    setTotalBalance: React.Dispatch<React.SetStateAction<{ account: string, name: string, total: number }[]>>;
    setTotalTrade: React.Dispatch<React.SetStateAction<number>>;
    setTradeHistoryChartData: React.Dispatch<React.SetStateAction<{ label: string; name: string; data: { x: Date, y: number }[] }[]>>;
    setAssignedAccountUser?: React.Dispatch<React.SetStateAction<{ user: { id: string, name: string, email: string }, account: { id: string, login_id: string, nickname: string, platform: string, broker: string, balance: number } }[] | undefined>>;
};

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider = ({ children }: { children: ReactNode }) => {
    // Define global state variables
    const [users, setUsers] = useState<User[]>([]);
    const [tradeAccounts, setTradeAccounts] = useState<TradeAccount[]>([]);
    const [totalAccounts, setTotalAccounts] = useState<number>(0);
    const [activeUsers, setActiveUsers] = useState<number>(0);
    const [allAccountBalance, setAllAccountBalance] = useState<number>(0);
    const [total_balance, setTotalBalance] = useState<{ account: string, name: string, total: number }[]>([]);
    const [total_trade, setTotalTrade] = useState<number>(0);
    const [tradeHistoryChartData, setTradeHistoryChartData] = useState<{ label: string; name: string; data: { x: Date, y: number }[] }[]>([]);
    const [assignedAccountUser, setAssignedAccountUser] = useState<{
        user: { id: string, name: string, email: string },
        account: { id: string, login_id: string, nickname: string, platform: string, broker: string, balance: number }
    }[]>();

    // Function to fetch data from the API
    async function getAssignedAccountUserData() {
        const response = await fetch(`${API_URL}api/v1/be/assigned_accounts`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        const result = await response.json();
        if (result.data) {
            setAssignedAccountUser(result.data);
        }
    }
    // Get data when init
    async function getDashboardBEStastics() {
        const response = await fetch(`${API_URL}api/v1/be/stats`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        const result = await response.json();
        if (result.status && result.data) {
            setTotalAccounts(result.data.total_accounts);
            setActiveUsers(result.data.active_users);
            setAllAccountBalance(result.data.total_balance);
            setTotalTrade(result.data.total_trades);
        }
    }

    async function getListUsers() {
        const response = await fetch(`${API_URL}api/v1/be/users`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        const result = await response.json();
        if (result.data) {
            setUsers(result.data);
        }
    }


    React.useEffect(() => {
        getListUsers();
        //getDashboardBEStastics();
        //getAssignedAccountUserData();
    }, []);

    return (
        <DataContext.Provider
            value={{
                users,
                tradeAccounts,
                totalAccounts,
                allAccountBalance,
                activeUsers,
                total_balance,
                total_trade,
                tradeHistoryChartData,
                assignedAccountUser,
                setUsers,
                setTradeAccounts,
                setTotalBalance,
                setTotalTrade,
                setTradeHistoryChartData,
                setAssignedAccountUser
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export const useData = (): DataContextType => {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};

export type { TradeAccount };